import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Camera, Check } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { PetAvatar } from '@/components/PetAvatar';
import { toggleSound, isSoundEnabled } from '@/utils/sounds';
import { EVOLUTION_STAGES, getStageFromStars, starsToNextStage } from '@/constants/petConstants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEVELS = [0, 10, 30, 60, 120, 220, 380, 600, 900, 1400];

const BOARD_OPTIONS = [
  { value: 'cbse',   label: 'CBSE / NCERT' },
  { value: 'ts_ssc', label: 'Telangana SSC' },
  { value: 'ap_ssc', label: 'AP SSC' },
  { value: 'icse',   label: 'ICSE' },
  { value: 'ib',     label: 'International Baccalaureate (IB)' },
];

const CLASS_OPTIONS = [
  { value: 'class_1_2', label: 'Class 1–2' },
  { value: 'class_3_4', label: 'Class 3–4' },
  { value: 'class_5_6', label: 'Class 5–6' },
  { value: 'class_7_8', label: 'Class 7–8' },
];

const LANGUAGE_OPTIONS = [
  { value: 'te',    label: 'Telugu' },
  { value: 'hi',    label: 'Hindi' },
  { value: 'ta',    label: 'Tamil' },
  { value: 'kn',    label: 'Kannada' },
  { value: 'ml',    label: 'Malayalam' },
  { value: 'mr',    label: 'Marathi' },
  { value: 'bn',    label: 'Bengali' },
  { value: 'gu',    label: 'Gujarati' },
  { value: 'or',    label: 'Odia' },
  { value: 'as',    label: 'Assamese' },
  { value: 'en-IN', label: 'English (India)' },
];

const DOT_COLOR = (pct) => {
  if (pct >= 75) return '#66BB6A';
  if (pct >= 50) return '#448AFF';
  if (pct >= 25) return '#FFD740';
  return 'rgba(0,0,0,0.15)';
};

const selectStyle = {
  background: '#FFFFFF',
  color: '#1A1A2E',
  border: '1.5px dashed #CCCCCC',
  borderRadius: 8,
  padding: '10px 14px',
  fontSize: 16,
  fontWeight: 500,
  cursor: 'pointer',
  outline: 'none',
  maxWidth: 220,
};

const SettingRow = ({ label, children }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 0', minHeight: 56,
    borderBottom: '1px dashed #EEEEEE',
  }}>
    <span style={{ fontSize: 16, color: '#555555', fontWeight: 500 }}>{label}</span>
    {children}
  </div>
);

const StatCard = ({ icon, value, label }) => (
  <div style={{
    flex: 1, background: '#FFFFFF',
    border: '1.5px dashed #CCCCCC',
    borderRadius: 4, padding: 20, textAlign: 'center', minHeight: 100,
  }}>
    <div style={{ fontSize: 28, marginBottom: 4 }}>{icon}</div>
    <div style={{ fontSize: 28, fontWeight: 600, fontFamily: "'Inter', sans-serif", color: '#1A1A2E', lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: 14, color: '#666666', marginTop: 6, fontWeight: 500, letterSpacing: 1 }}>
      {label}
    </div>
  </div>
);

export default function Profile({ user, masteryTopics = [], onSignIn, onLanguageChange }) {
  const { signOut } = useAuth();
  const avatarFileRef = useRef(null);
  const [avatarUploadState, setAvatarUploadState] = useState('idle'); // idle | uploading | success | error
  const [avatarError, setAvatarError] = useState('');

  // Read from localStorage
  const displayName  = localStorage.getItem('you_display_name') || (user?.email?.split('@')[0] || 'You');
  const customAvatar = localStorage.getItem('you_custom_avatar') || '';
  const googleAvatar = localStorage.getItem('you_google_avatar') || '';
  const avatarUrl    = customAvatar || googleAvatar;
  const petType      = localStorage.getItem('you_pet_type') || 'elephant';
  const petName      = localStorage.getItem('you_pet_name') || 'your pet';
  const totalXP      = parseInt(localStorage.getItem('you_total_xp') || '0');
  const currentLevel = parseInt(localStorage.getItem('you_current_level') || '0');
  const streak       = parseInt(localStorage.getItem('you_streak_count') || '0');
  const storiesCount = parseInt(localStorage.getItem('you_stories_generated') || '0');
  const petStars     = parseInt(localStorage.getItem('you_pet_stars') || '0');
  const petStage     = parseInt(localStorage.getItem('you_pet_stage') || '1');

  const [board,    setBoard]    = useState(localStorage.getItem('you_board')    || 'cbse');
  const [classGrp, setClassGrp] = useState(localStorage.getItem('you_class')    || 'class_5_6');
  const [language, setLanguage] = useState(localStorage.getItem('you_language') || 'te');
  const [soundOn,  setSoundOn]  = useState(isSoundEnabled());

  // XP bar (legacy display)
  const levelStart = LEVELS[currentLevel] ?? 0;
  const levelEnd   = LEVELS[currentLevel + 1] ?? levelStart + 500;

  // Star / stage bar
  const stageName   = EVOLUTION_STAGES[petStage - 1]?.name || 'Baby';
  const starsLeft   = starsToNextStage(petStars);
  const nextStageTotal = EVOLUTION_STAGES.find(s => s.totalStars > petStars)?.totalStars ?? petStars;
  const starBarPct  = starsLeft > 0 ? Math.round((petStars / nextStageTotal) * 100) : 100;

  // Evolution history
  const evoHistory  = EVOLUTION_STAGES.map((s, i) => ({
    ...s,
    reached: petStage >= s.stage,
    current: petStage === s.stage,
  }));

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setAvatarUploadState('error');
      setAvatarError('Only JPG, PNG, or WebP photos work');
      setTimeout(() => setAvatarUploadState('idle'), 3000);
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setAvatarUploadState('error');
      setAvatarError('Photo must be under 2MB');
      setTimeout(() => setAvatarUploadState('idle'), 3000);
      return;
    }

    setAvatarUploadState('uploading');
    setAvatarError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.includes('auth-token'));
      const session = token ? JSON.parse(localStorage.getItem(token)) : null;
      const headers = {};
      if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 30000);

      const res = await fetch(`${API}/upload-avatar`, {
        method: 'POST',
        headers,
        body: formData,
        signal: controller.signal,
      });
      clearTimeout(timeout);

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Upload failed');
      }

      const data = await res.json();
      localStorage.setItem('you_custom_avatar', data.avatar_url);
      setAvatarUploadState('success');
      setTimeout(() => setAvatarUploadState('idle'), 1500);
    } catch (err) {
      setAvatarUploadState('error');
      setAvatarError(
        err.name === 'AbortError'
          ? 'Taking too long. Check your internet and try again.'
          : (err.message || 'Something went wrong. Try again.')
      );
      setTimeout(() => setAvatarUploadState('idle'), 3000);
    }

    // Reset file input so same file can be re-selected
    if (avatarFileRef.current) avatarFileRef.current.value = '';
  };

  const handleBoard = (v)    => { setBoard(v);    localStorage.setItem('you_board', v); };
  const handleClass = (v)    => { setClassGrp(v); localStorage.setItem('you_class', v); };
  const handleLang  = (v)    => {
    setLanguage(v);
    localStorage.setItem('you_language', v);
    if (onLanguageChange) onLanguageChange(v);
  };
  const handleSound = ()     => {
    const next = !soundOn;
    setSoundOn(next);
    toggleSound(next);
  };

  // Initials avatar fallback
  const initials = displayName.slice(0, 2).toUpperCase();
  const boardLabel    = BOARD_OPTIONS.find(b => b.value === board)?.label || board;
  const classLabel    = CLASS_OPTIONS.find(c => c.value === classGrp)?.label || classGrp;

  return (
    <div style={{ padding: '16px 16px 140px', maxWidth: 720, margin: '0 auto', width: '100%' }}>

      {/* ── User header ─────────────────────────────── */}
      <div style={{
        position: 'relative',
        background: '#FFFFFF',
        border: '1.5px dashed #CCCCCC', borderLeft: '3px solid #448AFF',
        borderRadius: 4, padding: 14,
        display: 'flex', alignItems: 'center', gap: 14,
        marginBottom: 14,
      }}>
        {/* Avatar with camera overlay */}
        <div
          onClick={() => user && avatarFileRef.current?.click()}
          style={{
            position: 'relative', width: 72, height: 72, flexShrink: 0,
            cursor: user ? 'pointer' : 'default',
          }}
          role={user ? 'button' : undefined}
          aria-label={user ? 'Change profile photo' : undefined}
        >
          <input
            type="file"
            ref={avatarFileRef}
            onChange={handleAvatarUpload}
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            style={{ display: 'none' }}
            aria-label="Upload profile photo"
          />
          {/* Avatar image or initials */}
          {avatarUrl ? (
            <img
              src={avatarUrl} alt={displayName}
              referrerPolicy="no-referrer"
              style={{
                width: 68, height: 68, borderRadius: '50%', objectFit: 'cover',
                opacity: avatarUploadState === 'uploading' ? 0.5 : 1,
                transition: 'opacity 0.2s',
              }}
            />
          ) : (
            <div style={{
              width: 68, height: 68, borderRadius: '50%',
              background: '#448AFF',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22, fontWeight: 500, color: '#fff',
              opacity: avatarUploadState === 'uploading' ? 0.5 : 1,
              transition: 'opacity 0.2s',
            }}>
              {initials}
            </div>
          )}
          {/* Upload spinner overlay */}
          {avatarUploadState === 'uploading' && (
            <div style={{
              position: 'absolute', inset: 0, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                style={{
                  width: 20, height: 20, borderRadius: '50%',
                  border: '2px solid transparent', borderTopColor: '#448AFF',
                }}
              />
            </div>
          )}
          {/* Success checkmark overlay */}
          {avatarUploadState === 'success' && (
            <motion.div
              initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
              style={{
                position: 'absolute', inset: 0, borderRadius: '50%',
                background: 'rgba(102,187,106,0.85)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Check size={20} color="#fff" strokeWidth={3} />
            </motion.div>
          )}
          {/* Camera icon badge — only for signed-in users */}
          {user && avatarUploadState === 'idle' && (
            <div style={{
              position: 'absolute', bottom: -2, right: -2,
              width: 32, height: 32, borderRadius: '50%',
              background: '#448AFF', border: '2px solid #FFFFFF',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Camera size={16} color="#fff" strokeWidth={2.5} />
            </div>
          )}
        </div>
        {/* Avatar upload status message */}
        <div aria-live="polite" style={{ position: 'absolute', bottom: -16, left: 14 }}>
          {avatarUploadState === 'uploading' && (
            <span style={{ fontSize: 11, color: '#448AFF' }}>Uploading...</span>
          )}
          {avatarUploadState === 'error' && avatarError && (
            <motion.span
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              style={{ fontSize: 11, color: '#D32F2F', whiteSpace: 'nowrap' }}
            >
              {avatarError}
            </motion.span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 20, fontWeight: 400, fontFamily: "'Patrick Hand', cursive", color: '#1A1A2E', margin: '0 0 5px 0',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayName}
          </p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 14, fontWeight: 500, padding: '6px 14px', borderRadius: 20,
              background: 'rgba(68,138,255,0.1)', color: '#448AFF',
            }}>{classLabel}</span>
            <span style={{
              fontSize: 14, fontWeight: 500, padding: '6px 14px', borderRadius: 20,
              background: 'rgba(255,215,64,0.1)', color: '#E6A800',
            }}>{boardLabel}</span>
          </div>
        </div>
      </div>

      {/* ── Pet section ──────────────────────────────── */}
      <div style={{
        background: '#FFFFFF',
        border: '1.5px dashed #CCCCCC', borderLeft: '3px solid #FFD740',
        borderRadius: 4, padding: '16px 14px',
        marginBottom: 14,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
      }}>
        <PetAvatar petType={petType} stage={petStage} mood="happy" size={160} animated/>
        {/* Pet name badge */}
        <div style={{
          background: '#FFD740', color: '#1A1A2E',
          borderRadius: 20, padding: '6px 20px',
          fontSize: 20, fontWeight: 400, fontFamily: "'Patrick Hand', cursive",
        }}>
          {petName}
        </div>
        {/* Stage name */}
        <div style={{ fontSize: 16, color: '#448AFF', fontWeight: 500 }}>
          Stage {petStage}: {stageName}
        </div>
        {/* Star count */}
        <div style={{ fontSize: 16, color: '#555555' }}>
          {petStars} ⭐ total
        </div>
        {/* Star progress bar */}
        <div style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 11, color: '#666666' }}>{petStars} ⭐</span>
            <span style={{ fontSize: 11, color: '#666666' }}>
              {starsLeft > 0 ? `${starsLeft} more to evolve` : 'Legendary! 🌟'}
            </span>
          </div>
          <div style={{ height: 5, borderRadius: 3, background: 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${starBarPct}%` }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
              style={{ height: '100%', borderRadius: 3, background: '#448AFF' }}
            />
          </div>
        </div>
        {/* Evolution history */}
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
          {evoHistory.map((s, i) => (
            <span key={s.stage} style={{
              fontSize: 11, fontWeight: s.current ? 600 : 400,
              color: s.reached ? '#E6A800' : 'rgba(0,0,0,0.2)',
            }}>
              {s.name}{i < evoHistory.length - 1 ? ' →' : ''}
            </span>
          ))}
        </div>
      </div>

      {/* ── 3 stat cards ─────────────────────────────── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <StatCard icon="🔥" value={streak}       label="STREAK" />
        <StatCard icon="✨" value={totalXP}      label="TOTAL XP" />
        <StatCard icon="📚" value={storiesCount} label="STORIES" />
      </div>

      {/* ── Concepts list ─────────────────────────────── */}
      {masteryTopics.length > 0 && (
        <div style={{
          background: '#FFFFFF',
          border: '1.5px dashed #CCCCCC', borderLeft: '3px solid #66BB6A',
          borderRadius: 4, padding: '12px 14px',
          marginBottom: 14,
        }}>
          <p style={{ fontSize: 14, fontFamily: "'Patrick Hand', cursive", fontWeight: 400, color: '#555555', margin: '0 0 12px 0', letterSpacing: 0.5 }}>
            CONCEPTS
          </p>
          {masteryTopics.slice(0, 5).map((t, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              marginBottom: i < Math.min(masteryTopics.length, 5) - 1 ? 8 : 0,
            }}>
              <div style={{
                width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                background: DOT_COLOR(t.mastery_pct),
              }} />
              <span style={{
                fontSize: 15, color: '#555555',
                flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {(t.content_preview || 'Topic').slice(0, 38)}
                {(t.content_preview?.length || 0) > 38 ? '…' : ''}
              </span>
              <span style={{ fontSize: 14, fontWeight: 600, color: DOT_COLOR(t.mastery_pct), flexShrink: 0 }}>
                {t.mastery_pct}%
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── Settings ──────────────────────────────────── */}
      <div style={{
        background: '#FFFFFF',
        border: '1.5px dashed #CCCCCC', borderLeft: '3px solid #AB47BC',
        borderRadius: 4, padding: '4px 14px',
        marginBottom: 14,
      }}>
        <SettingRow label="Board">
          <select value={board} onChange={e => handleBoard(e.target.value)} style={selectStyle}>
            {BOARD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </SettingRow>

        <SettingRow label="Class">
          <select value={classGrp} onChange={e => handleClass(e.target.value)} style={selectStyle}>
            {CLASS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </SettingRow>

        <SettingRow label="Language">
          <select value={language} onChange={e => handleLang(e.target.value)} style={selectStyle}>
            {LANGUAGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </SettingRow>

        <SettingRow label="Sound effects">
          <div
            onClick={handleSound}
            style={{
              width: 36, height: 20, borderRadius: 10, position: 'relative',
              background: soundOn ? '#448AFF' : 'rgba(0,0,0,0.12)',
              transition: 'background 0.2s', cursor: 'pointer', flexShrink: 0,
            }}
          >
            <div style={{
              position: 'absolute', top: 3, left: soundOn ? 18 : 3,
              width: 14, height: 14, borderRadius: '50%', background: '#fff',
              transition: 'left 0.2s',
            }} />
          </div>
        </SettingRow>

        <SettingRow label="My Pet">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <PetAvatar size={28} state="idle" petType={petType} />
            <span style={{ fontSize: 16, color: '#555555', fontWeight: 500 }}>{petName}</span>
          </div>
        </SettingRow>
      </div>

      {/* ── Auth button ───────────────────────────────── */}
      {user ? (
        <button
          onClick={signOut}
          style={{
            width: '100%', padding: '16px 0', borderRadius: 12, border: 'none',
            background: 'rgba(255,80,80,0.12)',
            color: '#D32F2F', fontSize: 16, fontWeight: 500, cursor: 'pointer', minHeight: 48,
          }}
        >
          Sign Out
        </button>
      ) : (
        <button
          onClick={onSignIn}
          style={{
            width: '100%', padding: '16px 0', borderRadius: 12, border: 'none',
            background: '#448AFF', fontFamily: "'Patrick Hand', cursive",
            color: '#fff', fontSize: 16, fontWeight: 400, cursor: 'pointer', minHeight: 48,
          }}
        >
          🚀 Create account — it's free
        </button>
      )}

    </div>
  );
}
